import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, CheckCircle } from 'lucide-react';

const MemoryGame = ({ category, words, onUpdateProgress, onFinish, onBack }) => {
    const [cards, setCards] = useState([]);
    const [flippedIndices, setFlippedIndices] = useState([]);
    const [matchedPairs, setMatchedPairs] = useState([]);
    const [isGameOver, setIsGameOver] = useState(false);
    const [isLocked, setIsLocked] = useState(false);
    const [targetPairs, setTargetPairs] = useState(0);

    const initializeGame = () => {
        // Initialize cards by picking up to 6 random words
        const shuffledWords = [...words].sort(() => Math.random() - 0.5);
        const selectedWords = shuffledWords.slice(0, 6);
        setTargetPairs(selectedWords.length);

        let initialCards = [];
        selectedWords.forEach(word => {
            // Pair 1
            initialCards.push({
                ...word,
                uniqueId: `${word.id}-1`
            });
            // Pair 2
            initialCards.push({
                ...word,
                uniqueId: `${word.id}-2`
            });
        });

        // Shuffle cards
        initialCards.sort(() => Math.random() - 0.5);
        setCards(initialCards);

        // Reset state
        setFlippedIndices([]);
        setMatchedPairs([]);
        setIsGameOver(false);
        setIsLocked(false);
    };

    useEffect(() => {
        initializeGame();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [category.id]);

    const handleCardClick = (index) => {
        if (isLocked || flippedIndices.includes(index) || matchedPairs.includes(cards[index].id)) {
            return;
        }

        const newFlipped = [...flippedIndices, index];
        setFlippedIndices(newFlipped);

        if (newFlipped.length === 2) {
            setIsLocked(true);
            const card1 = cards[newFlipped[0]];
            const card2 = cards[newFlipped[1]];

            if (card1.id === card2.id) {
                // Match!
                setTimeout(() => {
                    setMatchedPairs(prev => [...prev, card1.id]);
                    setFlippedIndices([]);
                    setIsLocked(false);
                    onUpdateProgress(card1.id, true);

                    if (matchedPairs.length + 1 === targetPairs) {
                        setIsGameOver(true);
                    }
                }, 1000); // 1 second delay to celebrate
            } else {
                // No match
                setTimeout(() => {
                    setFlippedIndices([]);
                    setIsLocked(false);
                    onUpdateProgress(card1.id, false);
                    onUpdateProgress(card2.id, false);
                }, 1500); // 1.5 second delay before flipping back
            }
        }
    };

    const handleBack = () => {
        onBack();
    };

    if (isGameOver) {
        return (
            <div className="w-full h-full flex flex-col items-center justify-center p-8 text-center bg-white rounded-3xl shadow-lg border-4 border-[#CAF0F8]">
                <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", bounce: 0.5 }}
                >
                    <CheckCircle size={100} className="text-green-500 mb-6 mx-auto" />
                </motion.div>
                <h2 className="text-4xl font-black text-slate-800 mb-4">¡Felicidades!</h2>
                <p className="text-xl text-slate-600 mb-8">¡Encontraste todas las parejas!</p>

                <div className="flex gap-4">
                    <button
                        onClick={onBack}
                        className="bg-slate-200 text-slate-700 font-bold text-xl py-4 px-8 rounded-full shadow-lg hover:shadow-xl hover:bg-slate-300 transition-all active:scale-95"
                    >
                        Volver al menú
                    </button>
                    <button
                        onClick={initializeGame}
                        className="bg-[#0073E4] text-white font-bold text-xl py-4 px-8 rounded-full shadow-lg hover:shadow-xl hover:bg-[#005bb5] transition-all active:scale-95"
                    >
                        Jugar de nuevo
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full flex-grow flex flex-col items-center pb-12">
            <div className={`w-full max-w-2xl bg-white rounded-[2rem] shadow-xl overflow-hidden shadow-${category.color.replace('bg-', '')}/30`}>

                {/* Header */}
                <div className={`${category.color} p-4 flex items-center justify-between`}>
                    <button
                        onClick={handleBack}
                        className={`bg-white/40 p-2 rounded-full hover:bg-white/60 transition-colors ${category.textColor}`}
                    >
                        <ArrowLeft size={24} />
                    </button>
                    <h2 className={`text-2xl font-black ${category.textColor} flex items-center`}>
                        {category.name}
                    </h2>
                    <div className="w-10"></div> {/* Placeholder for balance */}
                </div>

                {/* Game Board */}
                <div className="p-8">
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                        {cards.map((card, index) => {
                            const isFlipped = flippedIndices.includes(index) || matchedPairs.includes(card.id);

                            return (
                                <div
                                    key={card.uniqueId}
                                    className="aspect-square relative cursor-pointer"
                                    onClick={() => handleCardClick(index)}
                                >
                                    <motion.div
                                        className="w-full h-full"
                                        initial={false}
                                        animate={{ rotateY: isFlipped ? 180 : 0 }}
                                        transition={{ duration: 0.6, type: 'spring', stiffness: 260, damping: 20 }}
                                        style={{ transformStyle: 'preserve-3d' }}
                                    >
                                        {/* Back of Card (Hidden side, logo) */}
                                        <div
                                            className="absolute w-full h-full backface-hidden rounded-xl bg-slate-200 shadow-md border-4 border-white flex items-center justify-center"
                                            style={{ backfaceVisibility: 'hidden' }}
                                        >
                                            <div className="w-12 h-12 bg-[#0073E4]/20 rounded-full flex items-center justify-center">
                                                <span className="text-[#0073E4] font-black text-2xl">?</span>
                                            </div>
                                        </div>

                                        {/* Front of Card (Visible image side) */}
                                        <div
                                            className={`absolute w-full h-full backface-hidden rounded-xl shadow-md border-4 border-white overflow-hidden flex flex-col items-center justify-center bg-[#f8f9fa]`}
                                            style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
                                        >
                                            {card.imagePath ? (
                                                <img
                                                    src={card.imagePath}
                                                    alt={card.word}
                                                    className="w-16 h-16 object-contain mb-1 drop-shadow-sm"
                                                    onError={(e) => { e.target.style.display = 'none'; }}
                                                />
                                            ) : (
                                                <div className="mb-1">
                                                    {React.cloneElement(card.Icon, { size: 48 })}
                                                </div>
                                            )}
                                            {card.word && (
                                                <span className="font-bold text-slate-700 text-sm leading-tight text-center px-1">
                                                    {card.word}
                                                </span>
                                            )}
                                            {card.sound && (
                                                <span className="font-bold text-[#0073E4] text-xs leading-tight text-center px-1">
                                                    "{card.sound}"
                                                </span>
                                            )}
                                        </div>
                                    </motion.div>
                                </div>
                            );
                        })}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default MemoryGame;
